export type CampaignModel = {
  address?: string,
  agency_id: number,
  agency_marketing: boolean,
  agents?: number[],
  agent_ids?: number[],
  agent_user_ids?: number[],
  agent_user_ids_map?: {
    [key: number]: number,
  },
  auction_date?: string,
  bathrooms?: string,
  bedrooms?: string,
  building_area_units?: string,
  building_area?: number,
  cache_key: number,
  campaign_category_id?: number,
  campaign_commercial_category_id?: number,
  campaign_duration?: string,
  campaign_fee?: number,
  campaign_rural_category_id?: number,
  campaign_sale_method_id?: number,
  campaign_status_id?: number,
  campaign_type_id?: number,
  canva_integrated?: boolean,
  car_spaces?: number,
  carparks?: string,
  carports?: string,
  council_rates?: string,
  country_code?: string,
  country_name?: string,
  data?: {
    agent_id?: string,
    external_api_id?: string,
    modified_time?: string,
    outgoing_id?: string,
    portal?: any,
    secondary_portal_id?: string,
    unique_id?: string,
    web_id?: string,
  },
  dates: {
    auction: {
      date: string,
      date_time: string,
    },
    start: {
      date: string,
    },
  },
  default_image?: string,
  dining_rooms?: string,
  end_date?: string,
  energy_rating?: string,
  extra_fields?: {
    additional_garaging?: [
      { key: string, value: string },
    ],
    chattels?: [
      { key: string, value: string },
    ],
    exterior?: [
      { key: string, value: string },
    ],
    flooring?: [
      { key: string, value: string },
    ],
    heating?: [
      { key: string, value: string },
    ],
    land_tax?: {
      value?: string,
      period?: string,
    },
    other_outgoings?: {
      value?: string,
      period?: string,
    },
    roof?: [
      { key: string, value: string },
    ],
    strata_admin?: {
      value?: string,
      period?: string,
    },
    strata_sinking?: {
      value?: string,
      period?: string,
    },
    water_rates?: {
      value?: string,
      period?: string,
    },
  },
  facilities?: number,
  frontage?: number,
  hidden: boolean,
  hide_address: boolean,
  hide_price?: boolean,
  hide_sold_price?: boolean,
  highest_search?: number,
  id: number,
  land_area?: number,
  land_area_units?: string,
  land_details?: string,
  land_zoning?: string,
  latitude?: string,
  legal_description?: string,
  living_areas?: string,
  longitude?: string,
  lot_number?: string,
  marketing_agency_id?: number,
  municipality?: string,
  paths?: {
    edit?: string,
  },
  post_code?: string,
  price?: number,
  price_guide?: string,
  priority_campaign_user_ids?: number[],
  property_url?: string,
  public_token?: string,
  published: boolean,
  purchaser_aml_complete?: boolean,
  reconciliation_totals?: {
    approved_bill_total?: number,
    approved_invoice_total?: number,
    approved_order_count?: number,
    approved_order_total?: number,
    approved_quote_count?: number,
    approved_quote_total?: number,
    campaign_balance?: number,
    campaign_item_count?: number,
    campaign_item_total?: number,
    contribution_total?: number,
    credit_note_total?: number,
    in_credit?: boolean,
    item_count?: number,
    outstanding_contributions?: number,
    payment_total?: number,
    processing_fees?: number,
    received_contribution_total?: number,
    remaining_budget?: number,
    total_combined_spend?: number,
  },
  sandbox: boolean,
  site_name?: string,
  sold_date?: string,
  sold_price?: number,
  sold?: boolean,
  start_date?: string,
  state_id?: number,
  state_name?: string,
  street_name: string,
  street_number: string,
  studies?: string,
  suburb_id?: string,
  suburb_name: string,
  team_id?: number,
  title: '',
  under_offer?: boolean,
  unit_number?: string,
  vendor_aml_complete?: boolean,
  year_built?: string,
}

export type CampaignParamKey = keyof CampaignModel

export type CampaignRequestOptions = {
  include_campaign_agency?: boolean,
  include_campaign_priority_campaign_user_ids?: boolean,
  include_campaign_image?: boolean,
}

const state = {
  agency_id: undefined,
  agency_marketing: false,
  agent_ids: undefined,
  auction_date: '',
  bathrooms: '',
  bedrooms: '',
  building_area: '',
  building_area_units: '',
  campaign_category_id: undefined,
  campaign_status_id: undefined,
  campaign_type_id: undefined,
  campaign_rural_category_id: undefined,
  campaign_sale_method_id: undefined,
  canva_integrated: false,
  car_spaces: undefined,
  carparks: '',
  carports: '',
  council_rates: '',
  country_code: '',
  data: {},
  dining_rooms: '',
  end_date: '',
  energy_rating: '',
  extra_fields: {},
  facilities: '',
  frontage: null,
  hidden: false,
  hide_address: false,
  hide_price: false,
  hide_sold_price: false,
  id: null,
  land_area_units: '',
  land_area: null,
  land_details: '',
  land_zoning: '',
  latitude: '',
  legal_description: '',
  living_areas: '',
  longitude: '',
  lot_number: '',
  marketing_agency_id: undefined,
  municipality: '',
  post_code: '',
  price_guide: '',
  price: undefined,
  property_url: '',
  purchaser_aml_complete: undefined,
  site_name: '',
  sold_date: '',
  sold_price: undefined,
  sold: false,
  start_date: '',
  state_id: undefined,
  state_name: '',
  street_name: '',
  street_number: '',
  studies: '',
  suburb_id: '',
  suburb_name: '',
  team_id: undefined,
  title: '',
  under_offer: false,
  unit_number: '',
  vendor_aml_complete: undefined,
  year_built: '',
}

type FieldTypes = {
  [key in CampaignParamKey]: {
    key: keyof CampaignModel,
    inputType: 'datetime' | 'text' | 'number' | 'date' | 'boolean',
    label: string,
  }
}

export const fieldTypes: FieldTypes = {
  auction_date: { key: 'auction_date', inputType: 'datetime', label: 'Auction Date' },
  bathrooms: { label: 'Bathrooms', key: 'bathrooms', inputType: 'text' },
  bedrooms: { label: 'Bedrooms', key: 'bedrooms', inputType: 'text' },
  campaign_duration: { label: 'Campaign Duration', key: 'campaign_duration', inputType: 'text' },
  car_spaces: { label: 'Car Spaces', key: 'car_spaces', inputType: 'number' },
  carparks: { label: 'Carparks', key: 'carparks', inputType: 'text' },
  carports: { label: 'Carports', key: 'carports', inputType: 'text' },
  council_rates: { label: 'Council Rates', key: 'council_rates', inputType: 'text' },
  country_code: { label: 'Country Code', key: 'country_code', inputType: 'text' },
  country_name: { label: 'Country Name', key: 'country_name', inputType: 'text' },
  energy_rating: { label: 'Energy Rating', key: 'energy_rating', inputType: 'text' },
  facilities: { label: 'Facilities', key: 'facilities', inputType: 'number' },
  frontage: { label: 'Frontage', key: 'frontage', inputType: 'number' },
  highest_search: { label: 'Price High', key: 'highest_search', inputType: 'number' },
  // @ts-expect-error TS2353
  inspection_details: { label: 'Inspection Details', key: 'inspection_details', inputType: 'text' },
  land_area: { label: 'Land Area', key: 'land_area', inputType: 'number' },
  land_area_units: { label: 'Land Area Units', key: 'land_area_units', inputType: 'text' },
  land_details: { label: 'Land Details', key: 'land_details', inputType: 'text' },
  land_zoning: { label: 'Land Zoning', key: 'land_zoning', inputType: 'text' },
  latitude: { label: 'Latitude', key: 'latitude', inputType: 'number' },
  legal_description: { label: 'Legal Description', key: 'legal_description', inputType: 'text' },
  living_areas: { label: 'Living Areas', key: 'living_areas', inputType: 'text' },
  longitude: { label: 'Longitude', key: 'longitude', inputType: 'number' },
  lot_number: { label: 'Lot Number', key: 'lot_number', inputType: 'text' },
  lowest_search: { label: 'Price Low', key: 'lowest_search', inputType: 'number' },
  municipality: { label: 'Municipality', key: 'municipality', inputType: 'text' },
  post_code: { label: 'Post Code', key: 'post_code', inputType: 'text' },
  price: { label: 'Price', key: 'price', inputType: 'number' },
  price_guide: { label: 'Price Guide', key: 'price_guide', inputType: 'text' },
  property_url: { label: 'Property Url', key: 'property_url', inputType: 'text' },
  published: { label: 'Published', key: 'published', inputType: 'boolean' },
  rental_bond: { label: 'Rental Bond', key: 'rental_bond', inputType: 'number' },
  rental_period: { label: 'Rental Period', key: 'rental_period', inputType: 'text' },
  site_name: { label: 'Site Name', key: 'site_name', inputType: 'text' },
  sold: { label: 'Sold', key: 'sold', inputType: 'boolean' },
  sold_price: { label: 'Sold Price', key: 'sold_price', inputType: 'number' },
  start_date: { label: 'Start Date', key: 'start_date', inputType: 'date' },
  state_name: { label: 'State Name', key: 'state_name', inputType: 'date' },
  street_name: { label: 'Street Name', key: 'street_name', inputType: 'text' },
  street_number: { label: 'Street Number', key: 'street_number', inputType: 'text' },
  studies: { label: 'Studies', key: 'studies', inputType: 'text' },
  suburb_name: { label: 'Suburb Name', key: 'suburb_name', inputType: 'text' },
  title: { label: 'Title', key: 'title', inputType: 'text' },
  under_offer: { label: 'Under Offer', key: 'under_offer', inputType: 'boolean' },
  unit_number: { label: 'Unit Number', key: 'unit_number', inputType: 'text' },
  year_built: { label: 'Year Built', key: 'year_built', inputType: 'text' },
}

export const requiredFields = [
  { key: 'agency_id' },
  { key: 'street_name' },
  { key: 'street_number' },
  { key: 'suburb_name' },
]

export default state
